/**
 * Slide-in side panel that shows the full status breakdown for a Stripe
 * Connect account row.
 *
 * Rationale: the grid only shows a rolled-up label like "Onboarding
 * Incomplete" with a yellow dot. Ops can't tell whether the problem is
 * "merchant hasn't started", "merchant submitted bank info but identity
 * verification is pending", or "Stripe rejected the EIN". This panel breaks
 * out every status flag and pulls the live Stripe requirements/capabilities
 * so ops can identify the exact blocker.
 *
 * The panel opens on the right side, takes ~480px, and overlays the grid
 * with a translucent backdrop. Click backdrop or Esc to close.
 */

import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import {
  refreshAccountStatus,
  migrateTerminalToPlatform,
  rollbackTerminalToConnected,
  setupVenueTerminal,
  setDestinationChargeFlow,
  undoVenueSetup,
  MigrateReaderResult,
} from "./api";
import { StripeConnectStatus, StripeProcessorRow } from "./types";
import ConfirmDialog from "../../components/ConfirmDialog";

interface Props {
  processor: StripeProcessorRow;
  onClose: () => void;
  /**
   * Called after each successful refreshAccountStatus call so the parent can
   * re-fetch its data and update the grid row. Without this the panel pulls
   * fresh status from Stripe but the grid row keeps showing the stale label
   * until the user reloads the page.
   */
  onRefreshed?: () => void;
}

// Map Stripe's snake_case requirement codes into human-readable strings.
// Anything not in this map is shown verbatim — ops can still copy it into a
// Stripe support ticket if needed.
const REQUIREMENT_LABELS: Record<string, string> = {
  "business_profile.mcc": "Business category (MCC code)",
  "business_profile.url": "Business website",
  "business_profile.support_email": "Support email",
  "business_profile.support_phone": "Support phone",
  "business_profile.product_description": "Product description",
  "business_type": "Business type (individual / company)",
  "company.address.line1": "Company street address",
  "company.address.city": "Company city",
  "company.address.state": "Company state",
  "company.address.postal_code": "Company ZIP / postal code",
  "company.name": "Company name",
  "company.phone": "Company phone",
  "company.tax_id": "Company tax ID (EIN)",
  "external_account": "Bank account",
  "individual.address.line1": "Personal street address",
  "individual.address.city": "Personal city",
  "individual.address.state": "Personal state",
  "individual.address.postal_code": "Personal ZIP / postal code",
  "individual.dob.day": "Date of birth",
  "individual.dob.month": "Date of birth",
  "individual.dob.year": "Date of birth",
  "individual.email": "Personal email",
  "individual.first_name": "Legal first name",
  "individual.last_name": "Legal last name",
  "individual.phone": "Personal phone",
  "individual.ssn_last_4": "SSN (last 4)",
  "individual.id_number": "Full SSN / ID number",
  "individual.verification.document": "Government ID document (front)",
  "individual.verification.additional_document": "Government ID document (back)",
  "tos_acceptance.date": "Terms of Service acceptance",
  "tos_acceptance.ip": "Terms of Service acceptance",
  "settings.payouts.statement_descriptor": "Bank statement descriptor",
};

function prettifyRequirement(key: string): string {
  return REQUIREMENT_LABELS[key] || key;
}

const Check = ({ ok, label, hint }: { ok: boolean; label: string; hint?: string }) => (
  <div className="flex items-start gap-2 py-1.5">
    {ok ? (
      <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
    ) : (
      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
    )}
    <div className="min-w-0">
      <div className={`text-sm ${ok ? "text-gray-800" : "text-gray-800 font-medium"}`}>
        {label}
      </div>
      {hint && !ok && (
        <div className="text-xs text-gray-500 mt-0.5">{hint}</div>
      )}
    </div>
  </div>
);

const SectionHeader = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-1">
    {children}
  </div>
);

const KeyValue = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex items-baseline gap-2 py-0.5">
    <div className="text-xs text-gray-500 w-32 shrink-0">{label}</div>
    <div className="text-xs text-gray-800 font-mono break-all">{value}</div>
  </div>
);

export function StripeAccountDetailsPanel({ processor, onClose, onRefreshed }: Props) {
  const [busy, setBusy] = useState(true);
  const [status, setStatus] = useState<StripeConnectStatus | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  // Terminal cutover (destination charge flow) state.
  const [cutoverBusy, setCutoverBusy] = useState(false);
  const [setupBusy, setSetupBusy] = useState(false);
  const [cutoverReaders, setCutoverReaders] = useState<MigrateReaderResult[] | null>(null);
  const [cutoverConfirm, setCutoverConfirm] = useState<
    null | "migrate" | "migrate-force" | "rollback"
  >(null);

  const config = processor.config || {};
  const connectedAccountId = config.connected_account_id || null;
  const mode = config.stripe_mode || null;
  // Note: we deliberately do NOT surface a link-type/account-type label in
  // the header. It carried no reliable deployability meaning (some OAuth-flow
  // accounts pass Stripe's controller check and deploy fine) and ops kept
  // reading it as a verdict. The Devices section shows Stripe's actual
  // answer, with the reason.

  const loadStatus = async () => {
    setBusy(true);
    try {
      const s = await refreshAccountStatus(processor.id);
      setStatus(s);
      setRefreshedAt(new Date());
      // Tell the parent grid so it can re-fetch and update the row's status
      // dot/label without requiring a full page reload.
      onRefreshed?.();
    } catch (err: any) {
      toast.error(`Could not refresh status: ${err?.message || "unknown"}`);
    } finally {
      setBusy(false);
    }
  };

  // Runs the confirmed Terminal cutover (or its rollback). The server does
  // all the safety work; this just relays results and refreshes the row.
  const runCutover = async () => {
    const kind = cutoverConfirm;
    setCutoverConfirm(null);
    if (!kind) return;
    setCutoverBusy(true);
    setCutoverReaders(null);
    try {
      if (kind === "migrate" || kind === "migrate-force") {
        // First attempt runs WITHOUT force so the server's zero-reader guard
        // can catch a wrong or stale source location. If the server refuses
        // because the venue genuinely has no readers yet (pre-provisioning),
        // the catch below opens a second, explicit confirmation — only that
        // re-run passes force:true.
        const r = await migrateTerminalToPlatform(
          processor.id,
          kind === "migrate-force" ? { force: true } : undefined
        );
        setCutoverReaders(r.readers);
        if (r.migrated) {
          toast.success("Cutover recorded — readers now live on Ronin's account");
        } else {
          toast.error(r.warning || "Migration stopped — see reader results");
        }
      } else {
        const r = await rollbackTerminalToConnected(processor.id);
        setCutoverReaders(r.readers);
        toast.success("Rolled back — tokens point at the merchant account again");
      }
      onRefreshed?.();
    } catch (err: any) {
      const msg: string = err?.message || "";
      if (kind === "migrate" && msg.includes("No readers found at")) {
        // Zero-reader guard tripped — ask before recording an empty cutover.
        setCutoverConfirm("migrate-force");
      } else {
        toast.error(msg || "Cutover call failed");
      }
    } finally {
      setCutoverBusy(false);
    }
  };

  // Auto-refresh on mount so the panel always shows the freshest data.
  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processor.id]);

  // Close on Esc.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Derive each "what's done / what's missing" check. Prefer fresh Stripe
  // data when available, fall back to what's in our DB.
  const detailsSubmitted = status?.detailsSubmitted ?? !!config.stripe_details_submitted;
  const chargesEnabled = status?.chargesEnabled ?? !!config.stripe_charges_enabled;
  const payoutsEnabled = status?.payoutsEnabled ?? !!config.stripe_payouts_enabled;
  const hasLocation = !!config.platform_account_location_id;
  const offlinePayments = !!config.platform_account_offline_payments_enabled;
  // Stripe stores reboot_window in the location's *local* time, not UTC.
  // We display it labeled as local so ops doesn't get confused. The legacy
  // `_utc` field name is checked as a fallback for old rows written before
  // we corrected the misunderstanding.
  const rebootWindow = (config.platform_account_reboot_window_local ||
    config.platform_account_reboot_window_utc) as
    | { start_hour: number; end_hour: number }
    | undefined;

  const requirements = status?.requirements || null;
  const cardPayments = status?.capabilities?.card_payments || null;
  const transfers = status?.capabilities?.transfers || null;

  // `tos_acceptance.*` is NOT an actionable step for OAuth (Standard)
  // accounts: the merchant agreed to Stripe's ToS during signup, and Stripe
  // back-fills the requirement automatically once identity verification
  // completes. Listing it under "due" sent ops and merchants hunting for an
  // accept button that doesn't exist on either side — so it's pulled out of
  // the actionable lists and shown as an informational note instead.
  const isTosRequirement = (r: string) => r.startsWith("tos_acceptance");
  const pastDueReqs = (requirements?.past_due || []).filter(
    (r) => !isTosRequirement(r)
  );
  const currentlyDueReqs = (requirements?.currently_due || []).filter(
    (r) => !isTosRequirement(r)
  );
  const pendingVerificationReqs = (
    requirements?.pending_verification || []
  ).filter((r) => !isTosRequirement(r));
  const tosInProgress = [
    ...(requirements?.past_due || []),
    ...(requirements?.currently_due || []),
    ...(requirements?.pending_verification || []),
  ].some(isTosRequirement);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {processor.payment_processor || "(unnamed processor)"}
            </div>
            <div className="text-xs text-gray-500 truncate">
              {processor.organization?.name || "(unknown org)"} · {mode || "(no mode)"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-2 p-1 rounded hover:bg-gray-100 text-gray-500"
            title="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {/* Connected account header info */}
          <SectionHeader>Connected account</SectionHeader>
          {connectedAccountId ? (
            <>
              <KeyValue label="Account ID" value={connectedAccountId} />
              {status?.country && <KeyValue label="Country" value={status.country} />}
              {status?.defaultCurrency && (
                <KeyValue label="Currency" value={status.defaultCurrency.toUpperCase()} />
              )}
              {status?.email && <KeyValue label="Email" value={status.email} />}
              {status?.businessProfile?.name && (
                <KeyValue label="Business name" value={status.businessProfile.name} />
              )}
            </>
          ) : (
            <div className="text-sm text-gray-500 italic">No connected Stripe account on this row yet.</div>
          )}

          {/* Onboarding checklist */}
          <SectionHeader>Onboarding</SectionHeader>
          <Check
            ok={detailsSubmitted}
            label="Business details submitted to Stripe"
            hint="Merchant hasn't completed Stripe's onboarding form. Click Continue on the row, or have the merchant log in to stripe.com to finish."
          />
          <Check
            ok={chargesEnabled}
            label="Can accept card payments"
            hint="Stripe hasn't enabled charges yet. See Stripe requirements below for the specific blocker."
          />
          <Check
            ok={payoutsEnabled}
            label="Can receive payouts to bank"
            hint="Stripe hasn't enabled payouts yet. Usually means the bank account or ID verification is incomplete."
          />

          {/* Devices — can Apps on Devices deploy the Ronin POS app here?
              Authoritative answer comes from Stripe (controller.is_controller
              via refreshAccountStatus); while loading, fall back to the local
              link-type heuristic (oauth rows are never deployable). */}
          {connectedAccountId && (
            <>
              <SectionHeader>Devices (Ronin POS app)</SectionHeader>
              {status?.controller ? (
                <>
                  <Check
                    ok={!!status.isController}
                    label={
                      status.isController
                        ? "Readers on this account can run the Ronin POS app"
                        : "Needs a new account — readers here can NOT run the Ronin POS app"
                    }
                    hint={
                      status.isController
                        ? undefined
                        : "Stripe reports this account is not controlled by a single platform — typical for pre-existing merchant accounts or ones connected to more than one platform. Control is fixed forever at creation, so devices moved here boot to the Stripe splash screen and its locations can't join our deploy groups. Create a replacement with 'New Stripe Connect Account', have the merchant re-onboard, then repoint events to the new processor."
                    }
                  />
                  {status.isController ? (
                    <div className="mt-1 mb-1 text-xs text-blue-800 border border-blue-200 bg-blue-50 rounded p-2 leading-snug">
                      <span className="font-semibold">
                        Eligible for deploy groups.
                      </span>{" "}
                      Add this account's Terminal location to a platform
                      deploy group so its readers get the app (and updates)
                      on-site.
                      {config.platform_account_location_id ? (
                        <>
                          {" "}
                          {config.destination_terminal_location_id ? (
                            // PLATFORM LOCATION. There is no account to search —
                            // it sits in Ronin's own Terminal list. The
                            // account-search step is Stripe's CONNECTED-account
                            // flow and returns nothing here.
                            <>
                              This venue's Terminal location is on{" "}
                              <strong>Ronin's own account</strong>, so there is no
                              account to search — pick it straight from the
                              platform's location list.
                            </>
                          ) : (
                            <>
                              Account to search:{" "}
                              <span className="font-mono">
                                {config.connected_account_id}
                              </span>
                            </>
                          )}
                          <div className="flex items-center gap-2 mt-1.5">
                            <button
                              type="button"
                              className="rounded border border-blue-300 bg-white px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100"
                              onClick={() => {
                                // For a CONNECTED-account location Stripe's
                                // "Add locations" search matches accounts, so a
                                // pasted tml_ returns nothing. For a PLATFORM
                                // location there is no account step at all, so
                                // the useful thing to copy is the location id.
                                const onPlatform =
                                  !!config.destination_terminal_location_id;
                                const value = onPlatform
                                  ? (config.platform_account_location_id as string)
                                  : (config.connected_account_id as string);
                                navigator.clipboard
                                  .writeText(value)
                                  .then(() =>
                                    toast.success(
                                      onPlatform
                                        ? "Location ID copied"
                                        : "Account ID copied"
                                    )
                                  )
                                  .catch(() => toast.error("Copy failed"));
                              }}
                            >
                              {config.destination_terminal_location_id
                                ? "Copy location ID"
                                : "Copy account ID"}
                            </button>
                            <a
                              // Deep link straight to the deploy-groups page,
                              // pinned to the Ronin platform account so it
                              // opens correctly even when the ops user's
                              // Stripe login has access to multiple accounts.
                              href="https://dashboard.stripe.com/acct_1QlwYyDKBVhj3Hkm/terminal/software/deploy_groups"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rounded border border-blue-300 bg-white px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100"
                            >
                              Open deploy groups ↗
                            </a>
                          </div>
                          <div className="mt-1.5">
                            {config.destination_terminal_location_id ? (
                              <>
                                In Stripe: Manage deploy groups → your S700 group
                                → Add locations → pick{" "}
                                <span className="font-mono">
                                  {config.platform_account_location_id}
                                </span>{" "}
                                from Ronin's own locations (no account search).
                              </>
                            ) : (
                              <>
                                In Stripe: Manage deploy groups → your S700 group
                                → Add locations → paste the account ID into the
                                search → pick this account's location (
                                <span className="font-mono">
                                  {config.platform_account_location_id}
                                </span>
                                ).
                              </>
                            )}{" "}
                            Do this BEFORE any reader registers here — reader
                            software reconciles installed apps on
                            re-registration, so a reader that registers to a
                            location outside the group loses the Ronin app and
                            waiting does not fix it. Stripe's API can't report
                            membership, so verify in the group's Locations tab.
                          </div>
                        </>
                      ) : (
                        <>
                          {" "}
                          No Terminal location is configured on this row yet —
                          create one first via Edit location, then come back
                          here to link it.
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="mt-1 mb-1 text-xs text-red-700 border border-red-200 bg-red-50 rounded p-2 leading-snug">
                      <span className="font-semibold">
                        Not eligible for deploy groups.
                      </span>{" "}
                      This account won't appear in the deploy group "Add
                      locations" account dropdown — Stripe only allows
                      locations from accounts controlled by a single platform.
                      <div className="mt-1">
                        <span className="font-semibold">Reason from Stripe:</span>{" "}
                        controller type is{" "}
                        <span className="font-mono">
                          {status.controller?.type || "unknown"}
                        </span>
                        {status.controller?.type === "account"
                          ? " — the merchant's account controls itself. It existed before (or outside of) Ronin, and Ronin only has authorized access to it, not control."
                          : " — Ronin is not the account's sole controlling platform."}
                        {config.stripe_link_type === "oauth"
                          ? " It was connected via the OAuth link flow."
                          : ""}
                      </div>
                      {status.accountCreated && (
                        <div className="mt-1">
                          <span className="font-semibold">Account created:</span>{" "}
                          {new Date(status.accountCreated * 1000).toLocaleDateString()}
                          {processor.created_at &&
                          new Date(status.accountCreated * 1000) <
                            new Date(processor.created_at)
                            ? ` — predates its Ronin connection (row created ${new Date(processor.created_at).toLocaleDateString()}), so it was never created under Ronin's control.`
                            : ""}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : config.stripe_is_controller === false ? (
                <Check
                  ok={false}
                  label="Stripe previously confirmed devices can't run the app here — refresh to re-check"
                  hint="On the last refresh, Stripe reported this account isn't controlled by a single platform, so it can't receive the Ronin POS app or join our deploy groups."
                />
              ) : busy ? (
                // Neutral while the Stripe lookup is in flight — a green check
                // here would claim an answer we don't have yet.
                <div className="flex items-start gap-2 py-1.5">
                  <ArrowPathIcon className="w-5 h-5 text-gray-400 shrink-0 mt-0.5 animate-spin" />
                  <div className="text-sm text-gray-500">
                    Checking deployability with Stripe…
                  </div>
                </div>
              ) : (
                <Check
                  ok={false}
                  label="Deployability unconfirmed — refresh from Stripe"
                  hint="The last Stripe lookup didn't return controller info. Click Refresh from Stripe below."
                />
              )}
            </>
          )}

          {/* Capabilities (live from Stripe) */}
          {(cardPayments || transfers) && (
            <>
              <SectionHeader>Stripe capabilities</SectionHeader>
              {cardPayments && (
                <Check
                  ok={cardPayments === "active"}
                  label={`Card payments: ${cardPayments}`}
                  hint={
                    cardPayments === "pending"
                      ? "Stripe is reviewing the application. No action needed unless this stays pending more than a few business days."
                      : cardPayments === "inactive"
                      ? "Capability has not been enabled. Check Stripe requirements below."
                      : undefined
                  }
                />
              )}
              {transfers && (
                <Check
                  ok={transfers === "active"}
                  label={`Transfers (payouts): ${transfers}`}
                  hint={
                    transfers === "pending"
                      ? "Stripe is reviewing — usually clears within a few business days."
                      : transfers === "inactive"
                      ? "Capability not enabled. Likely missing bank info or verification."
                      : undefined
                  }
                />
              )}
            </>
          )}

          {/* Stripe requirements list — the gold for ops */}
          {requirements && (
            <>
              {pastDueReqs.length > 0 && (
                <>
                  <SectionHeader>Past due (immediate action needed)</SectionHeader>
                  <ul className="text-sm text-red-700 list-disc pl-5 space-y-0.5">
                    {pastDueReqs.map((r) => (
                      <li key={`pd-${r}`}>
                        {prettifyRequirement(r)}
                        <span className="text-xs text-gray-500 ml-1 font-mono">({r})</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {currentlyDueReqs.length > 0 && (
                <>
                  <SectionHeader>Currently due</SectionHeader>
                  <ul className="text-sm text-yellow-800 list-disc pl-5 space-y-0.5">
                    {currentlyDueReqs.map((r) => (
                      <li key={`cd-${r}`}>
                        {prettifyRequirement(r)}
                        <span className="text-xs text-gray-500 ml-1 font-mono">({r})</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {pendingVerificationReqs.length > 0 && (
                <>
                  <SectionHeader>Pending Stripe verification</SectionHeader>
                  <ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                    {pendingVerificationReqs.map((r) => (
                      <li key={`pv-${r}`}>{prettifyRequirement(r)}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-gray-500 mt-1">
                    Stripe is reviewing these — no merchant action needed.
                  </div>
                </>
              )}
              {tosInProgress && (
                <>
                  <SectionHeader>Terms of Service</SectionHeader>
                  <div className="text-xs text-gray-600">
                    Already accepted during Stripe signup. Stripe records it
                    automatically once identity verification completes — there
                    is no accept button here or in the merchant's Stripe
                    dashboard, and nothing for anyone to do. This line
                    disappears on its own when verification finishes.
                  </div>
                </>
              )}
              {(requirements.eventually_due?.length ?? 0) > 0 && (
                <>
                  <SectionHeader>Eventually due</SectionHeader>
                  <ul className="text-sm text-gray-700 list-disc pl-5 space-y-0.5">
                    {requirements.eventually_due!.map((r) => (
                      <li key={`ed-${r}`}>{prettifyRequirement(r)}</li>
                    ))}
                  </ul>
                  <div className="text-xs text-gray-500 mt-1">
                    Not blocking yet, but Stripe will require these later.
                  </div>
                </>
              )}
              {requirements.disabled_reason && (
                <>
                  <SectionHeader>Disabled reason</SectionHeader>
                  <div className="text-sm text-red-700">
                    {requirements.disabled_reason.replace(/_/g, " ")}
                  </div>
                </>
              )}
              {(requirements.errors?.length ?? 0) > 0 && (
                <>
                  <SectionHeader>Verification errors</SectionHeader>
                  <ul className="text-sm text-red-700 space-y-1">
                    {requirements.errors!.map((e, i) => (
                      <li key={`err-${i}`}>
                        <div className="font-medium">
                          {e.requirement ? prettifyRequirement(e.requirement) : "(unknown field)"}
                        </div>
                        <div className="text-xs text-gray-600">{e.reason || e.code}</div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {(!requirements.past_due?.length &&
                !requirements.currently_due?.length &&
                !requirements.pending_verification?.length &&
                !requirements.eventually_due?.length &&
                !requirements.errors?.length) && (
                <>
                  <SectionHeader>Stripe requirements</SectionHeader>
                  <div className="text-sm text-green-700">
                    All Stripe requirements satisfied.
                  </div>
                </>
              )}
            </>
          )}

          {/* Terminal */}
          <SectionHeader>Terminal</SectionHeader>
          <Check
            ok={hasLocation}
            label="Terminal location configured"
            hint="Card readers won't work without a location. Click Add location on the row."
          />
          {hasLocation && (
            <KeyValue label="Location ID" value={config.platform_account_location_id as string} />
          )}
          <Check
            ok={offlinePayments}
            label="Offline payments enabled"
            hint="Offline mode will be enabled the next time you save the Terminal location."
          />
          {hasLocation && rebootWindow && (
            <KeyValue
              label="Reboot window"
              value={`${pad(rebootWindow.start_hour)}:00–${pad(rebootWindow.end_hour)}:00 local`}
            />
          )}

          {/* Charge flow / Terminal cutover.
              destination = charges land on RONIN's account (Ronin pays the
              Stripe processing fee; merchant receives net transfers). The
              cutover moves the venue's readers to Ronin's platform account —
              canary-safe server-side (stops at the first failed reader,
              skips Bluetooth readers, only records the cutover when every
              reader made it). Rollback reverses it. */}
          <SectionHeader>Charge flow</SectionHeader>
          <KeyValue
            label="Flow"
            value={
              config.stripe_charge_flow === "destination"
                ? "Destination (Ronin pays Stripe fees)"
                : "Direct (merchant pays Stripe fees)"
            }
          />
          {config.destination_terminal_location_id && (
            <>
              <KeyValue label="Readers live on" value="Ronin's platform account" />
              <KeyValue
                label="Card taps charge"
                value="Ronin (forwarded to the merchant)"
              />
            </>
          )}
          {config.terminal_setup_completed_at && (
            <KeyValue
              label="Set up"
              value={`${
                config.terminal_setup_mode === "destination"
                  ? "On Ronin's account"
                  : "On the merchant's account"
              }${config.terminal_setup_reason ? ` — ${config.terminal_setup_reason}` : ""}`}
            />
          )}
          {/* Venue setup. The backend decides WHICH account this venue's
              Terminal location belongs on — ops never picks. Creating the
              location is what makes card readers work; moving the venue's
              ONLINE revenue is the separate switch below, so all 121 venues
              can be staged now and flipped on a schedule. */}
          {!config.platform_account_location_id && (
            <div className="mt-2">
              <button
                type="button"
                disabled={setupBusy}
                onClick={async () => {
                  const line1 = window.prompt("Street address for this venue's Terminal location");
                  if (!line1) return;
                  const city = window.prompt("City") || "";
                  const state = window.prompt("State (required for US)") || "";
                  const postal_code = window.prompt("ZIP / postal code") || "";
                  const country = (window.prompt("Country code", "US") || "US").toUpperCase();
                  setSetupBusy(true);
                  try {
                    const r = await setupVenueTerminal(processor.id, processor.payment_processor, {
                      line1,
                      city,
                      state,
                      postal_code,
                      country,
                    });
                    toast.success(
                      r.placement === "platform"
                        ? `Set up on Ronin's account — ${r.locationDisplayName}`
                        : `Set up on the merchant's account — ${r.locationDisplayName}`
                    );
                    onRefreshed?.();
                  } catch (e: any) {
                    toast.error(e?.message || "Setup failed");
                  } finally {
                    setSetupBusy(false);
                  }
                }}
                className="rounded bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-50"
              >
                {setupBusy ? "Setting up…" : "Set up Terminal for this venue"}
              </button>
              <div className="mt-1 text-xs text-gray-500">
                Creates the Terminal location on whichever Stripe account this
                venue belongs on. Add it to the deploy group before any reader
                registers.
              </div>
            </div>
          )}

          {config.terminal_setup_completed_at &&
            !config.destination_terminal_location_id && (
              <div className="mt-2">
                <button
                  type="button"
                  disabled={setupBusy}
                  onClick={async () => {
                    setSetupBusy(true);
                    try {
                      await undoVenueSetup(processor.id);
                      toast.success("Setup undone");
                      onRefreshed?.();
                    } catch (e: any) {
                      toast.error(e?.message || "Undo failed");
                    } finally {
                      setSetupBusy(false);
                    }
                  }}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Undo setup
                </button>
              </div>
            )}

          {/* Card-not-present only. Card taps already follow wherever the
              readers were set up. */}
          {config.destination_terminal_location_id && (
            <div className="mt-2">
              <button
                type="button"
                disabled={setupBusy}
                onClick={async () => {
                  const turningOn = config.stripe_charge_flow !== "destination";
                  if (
                    turningOn &&
                    !window.confirm(
                      "This moves the venue's ONLINE revenue — ticketing, order ahead, invoicing, top-ups — onto Ronin's account. Card taps already route there. Continue?"
                    )
                  )
                    return;
                  setSetupBusy(true);
                  try {
                    await setDestinationChargeFlow(processor.id, turningOn);
                    toast.success(
                      turningOn
                        ? "Online revenue now routes through Ronin"
                        : "Online revenue returned to the merchant"
                    );
                    onRefreshed?.();
                  } catch (e: any) {
                    toast.error(e?.message || "Could not change the charge flow");
                  } finally {
                    setSetupBusy(false);
                  }
                }}
                className="rounded border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
              >
                {config.stripe_charge_flow === "destination"
                  ? "Return online revenue to the merchant"
                  : "Route online revenue through Ronin"}
              </button>
            </div>
          )}

          {config.stripe_charge_flow === "destination" && (
            <div className="mt-2 flex gap-2">
              {!config.destination_terminal_location_id ? (
                <button
                  type="button"
                  disabled={cutoverBusy}
                  onClick={() => setCutoverConfirm("migrate")}
                  className="rounded bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                >
                  {cutoverBusy ? "Migrating…" : "Migrate readers to Ronin account"}
                </button>
              ) : (
                <button
                  type="button"
                  disabled={cutoverBusy}
                  onClick={() => setCutoverConfirm("rollback")}
                  className="rounded border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {cutoverBusy ? "Rolling back…" : "Roll readers back to merchant account"}
                </button>
              )}
            </div>
          )}
          {cutoverReaders && (
            <ul className="mt-2 space-y-1 text-xs">
              {cutoverReaders.length === 0 && (
                <li className="text-gray-500">No readers to move (reader-less cutover).</li>
              )}
              {cutoverReaders.map((r, i) => (
                <li key={i} className={r.ok ? "text-green-700" : "text-red-700"}>
                  {r.serial || r.readerId || "reader"} —{" "}
                  {r.ok
                    ? r.bluetoothSkipped
                      ? "Bluetooth, follows the app automatically"
                      : "moved"
                    : r.skipped
                    ? "skipped (earlier reader failed)"
                    : r.error || "failed"}
                </li>
              ))}
            </ul>
          )}

          {/* Pricing snapshot */}
          <SectionHeader>Pricing</SectionHeader>
          {config.processing_rate != null && (
            <KeyValue
              label="Processing rate"
              value={`${(parseFloat(String(config.processing_rate)) * 100).toFixed(2)}%`}
            />
          )}
          {config.application_fee_per_transaction != null && (
            <KeyValue
              label="Per-transaction fee"
              value={`$${Number(config.application_fee_per_transaction).toFixed(2)}`}
            />
          )}

          {/* Activity */}
          <SectionHeader>Activity</SectionHeader>
          {config.stripe_onboarded_at && (
            <KeyValue
              label="Onboarded"
              value={new Date(config.stripe_onboarded_at).toLocaleString()}
            />
          )}
          <KeyValue
            label="Row created"
            value={new Date(processor.created_at).toLocaleString()}
          />
          {refreshedAt && (
            <KeyValue
              label="Last refresh"
              value={refreshedAt.toLocaleTimeString()}
            />
          )}
          {status?.warning && (
            <div className="mt-2 flex items-start gap-2 text-xs text-yellow-700 border border-yellow-300 bg-yellow-50 rounded p-2">
              <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{status.warning}</span>
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between">
          <div className="text-xs text-gray-500 flex items-center gap-1">
            {busy ? (
              <>
                <ArrowPathIcon className="w-3 h-3 animate-spin" />
                Refreshing...
              </>
            ) : refreshedAt ? (
              <>
                <ClockIcon className="w-3 h-3" />
                {refreshedAt.toLocaleTimeString()}
              </>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={loadStatus}
              disabled={busy}
              className="inline-flex items-center gap-1 text-xs font-medium px-3 h-8 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <ArrowPathIcon className="w-3 h-3" />
              Refresh from Stripe
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-xs font-medium px-3 h-8 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={cutoverConfirm !== null}
        title={
          cutoverConfirm === "rollback"
            ? "Roll readers back to the merchant account?"
            : cutoverConfirm === "migrate-force"
            ? "No readers found — record the cutover anyway?"
            : "Migrate this venue's readers to Ronin's account?"
        }
        message={
          cutoverConfirm === "rollback" ? (
            <span>
              Readers move back to the merchant's Stripe account and connection
              tokens flip immediately. Taps go back to charging the merchant
              (they pay Stripe's fee again). Run only between events.
            </span>
          ) : cutoverConfirm === "migrate-force" ? (
            <span>
              The venue's Terminal location has no readers on it. That's
              expected when devices haven't been placed yet (pre-provisioning)
              — but if this venue SHOULD have readers, stop here: the location
              on file is probably wrong, and recording the cutover would
              strand its readers on the merchant account. Proceed only for a
              genuinely reader-less venue.
            </span>
          ) : (
            <span>
              Moves this venue's smart readers (and Terminal location) onto
              Ronin's platform account so taps charge Ronin and the merchant
              never sees a Stripe fee. Safe by design: stops at the first
              failed reader, Bluetooth readers follow automatically, and the
              cutover is only recorded when every reader makes it. Readers
              can't take payments during the seconds they move — run between
              events.
            </span>
          )
        }
        confirmLabel={
          cutoverConfirm === "rollback"
            ? "Roll back"
            : cutoverConfirm === "migrate-force"
            ? "Record cutover anyway"
            : "Migrate readers"
        }
        busy={cutoverBusy}
        onConfirm={runCutover}
        onCancel={() => setCutoverConfirm(null)}
      />
    </>
  );
}

function pad(n: number) {
  return String(n).padStart(2, "0");
}
